import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Buildings, ShieldCheck, ClipboardText, CalendarBlank, ChartBar,
  Flag, CheckCircle, Bell, Sparkle, Camera, FolderOpen, Rocket,
  ArrowRight, CaretDown, EnvelopeSimple, MapPin, Play,
  Notebook, Users, Lightning, Certificate, ChatCircleDots,
  X, Cloud, DeviceMobile, GlobeStand, Lock, CaretLeft, CaretRight,
  Quotes, VideoCamera, ArrowsClockwise,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGIN_URL = 'https://chantix.pro/login';
const AXEL_AVATAR = '/axel/axel-avatar.jpg';
// Placeholder configurable (mis a jour ulterieurement par l'admin)
const DEMO_VIDEO_URL = ''; // URL YouTube/Vimeo (ex: 'https://www.youtube.com/embed/xxx')

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = '' }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function FeatureCard({ Icon, title, desc, accent = 'gold', delay = 0 }) {
  const accentClass = {
    gold:   'from-[#D4AF37]/20 to-transparent border-[#D4AF37]/30 text-[#D4AF37]',
    violet: 'from-purple-500/20 to-transparent border-purple-500/30 text-purple-400',
    white:  'from-white/10 to-transparent border-white/15 text-white',
  }[accent];
  return (
    <Reveal delay={delay}>
      <div className={`group relative overflow-hidden rounded-2xl border bg-gradient-to-br ${accentClass} p-6 backdrop-blur-sm hover:-translate-y-1 transition-transform duration-500`}>
        <div className="w-12 h-12 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
          <Icon size={22} weight="duotone" />
        </div>
        <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
        <p className="text-neutral-400 text-sm leading-relaxed">{desc}</p>
        <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-[#D4AF37]/5 blur-2xl group-hover:bg-[#D4AF37]/10 transition" />
      </div>
    </Reveal>
  );
}

function Benefit({ Icon, title, desc }) {
  return (
    <div className="flex gap-4 items-start">
      <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
        <Icon size={20} weight="duotone" className="text-[#D4AF37]" />
      </div>
      <div>
        <h4 className="text-white font-semibold mb-1">{title}</h4>
        <p className="text-neutral-400 text-sm">{desc}</p>
      </div>
    </div>
  );
}

function FAQItem({ q, a, idx, open, setOpen }) {
  const isOpen = open === idx;
  return (
    <div className="border-b border-white/10">
      <button
        onClick={() => setOpen(isOpen ? -1 : idx)}
        className="w-full flex items-center justify-between py-5 text-left hover:bg-white/[0.02] px-2 rounded-lg transition"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="text-white font-medium pr-4">{q}</span>
        <CaretDown size={18} className={`text-[#D4AF37] flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`grid transition-all duration-300 ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <p className="text-neutral-400 pb-5 px-2 leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

function ScreenshotsCarousel() {
  const [i, setI] = useState(0);
  const slides = [
    { src: '/screenshots/01-dashboard.jpg', title: 'Tableau de bord',           desc: 'Vos chantiers, réserves et actions rapides en un coup d\'œil.' },
    { src: '/screenshots/02-chantiers.jpg', title: 'Gestion des chantiers',     desc: 'Fiches détaillées avec statut, dates, MOA et accès rapide aux modules.' },
    { src: '/screenshots/03-reserves.jpg',  title: 'Réserves',                  desc: 'Priorisation, illustrations, localisation et suivi complet du cycle de vie.' },
    { src: '/screenshots/04-planning.jpg',  title: 'Planning Gantt',            desc: 'Ordonnancement par lot, avancement global et détection automatique des retards.' },
    { src: '/screenshots/05-axel.jpg',      title: 'AXEL — Assistant IA',       desc: 'Un chat qui connaît vos données et vous répond en français.' },
    { src: '/screenshots/06-documents.jpg', title: 'Documents & rapports',      desc: 'Génération PDF en 1 clic : Mensuel, Avancement, Compte Rendu, Planning.' },
  ];
  const next = () => setI((n) => (n + 1) % slides.length);
  const prev = () => setI((n) => (n - 1 + slides.length) % slides.length);

  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="py-24 px-6 border-t border-white/5 bg-black relative z-10" data-testid="screenshots-carousel">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div className="text-center mb-12">
            <span className="text-xs font-semibold tracking-widest text-[#D4AF37] uppercase">L&apos;interface en vrai</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-3">CHANTIX, écran par écran.</h2>
            <p className="text-neutral-400 mt-3">Découvrez la vraie interface de la plateforme avant même de vous inscrire.</p>
          </div>
        </Reveal>
        <Reveal delay={150}>
          <div className="relative">
            {/* Fenêtre navigateur */}
            <div className="rounded-2xl border border-white/10 bg-neutral-900 shadow-2xl overflow-hidden" data-testid={`slide-${i}`}>
              <div className="flex items-center justify-between px-4 py-3 bg-neutral-900 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-400" />
                  <span className="w-3 h-3 rounded-full bg-amber-400" />
                  <span className="w-3 h-3 rounded-full bg-emerald-400" />
                  <span className="ml-3 text-xs text-neutral-500">chantix.pro</span>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-neutral-500">{i + 1} / {slides.length}</span>
              </div>
              <div className="relative aspect-[16/10] bg-white">
                <img src={slides[i].src} alt={slides[i].title}
                  className="absolute inset-0 w-full h-full object-cover object-top" />
              </div>
              <div className="p-5 border-t border-white/10 bg-neutral-900">
                <h3 className="text-lg font-bold text-white">{slides[i].title}</h3>
                <p className="text-sm text-neutral-400 mt-1">{slides[i].desc}</p>
              </div>
            </div>
            {/* dots */}
            <div className="mt-6 flex items-center justify-center gap-2">
              {slides.map((_, idx) => (
                <button key={idx} onClick={() => setI(idx)}
                  className={`transition-all rounded-full ${idx === i ? 'w-8 h-2 bg-[#D4AF37]' : 'w-2 h-2 bg-white/20 hover:bg-white/40'}`}
                  data-testid={`slide-dot-${idx}`} aria-label={`Écran ${idx + 1}`} />
              ))}
            </div>
            <button onClick={prev}
              className="hidden md:flex absolute top-1/2 -translate-y-1/2 -left-6 w-12 h-12 rounded-full bg-black border border-white/10 items-center justify-center hover:bg-[#D4AF37] hover:text-black transition"
              data-testid="slide-prev" aria-label="Précédent">
              <CaretLeft size={22} weight="bold" />
            </button>
            <button onClick={next}
              className="hidden md:flex absolute top-1/2 -translate-y-1/2 -right-6 w-12 h-12 rounded-full bg-black border border-white/10 items-center justify-center hover:bg-[#D4AF37] hover:text-black transition"
              data-testid="slide-next" aria-label="Suivant">
              <CaretRight size={22} weight="bold" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function VitrinePage() {
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = 'CHANTIX — La plateforme intelligente pour CSPS, OPC & BTP';
  }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Merci de remplir nom, email et message');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/public/contact`, form);
      toast.success('Message envoyé ! Nous revenons vers vous rapidement.');
      setForm({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur — réessayez plus tard');
    } finally { setSending(false); }
  };

  const features = [
    { Icon: Buildings,    title: 'Gestion des chantiers',       desc: 'Toutes vos opérations centralisées : fiches, équipes, avancement, documents, en 1 vue claire.' },
    { Icon: ShieldCheck,  title: 'Missions CSPS',               desc: 'PGC, PPSPS, DIUO, visites, observations, signatures : conformes et générés automatiquement.', accent: 'gold' },
    { Icon: Notebook,     title: 'Missions OPC',                desc: 'Comptes rendus, entreprises, décisions, absences, avancement par lot — pilotez sans stress.', accent: 'white' },
    { Icon: CalendarBlank,title: 'Planning Gantt',              desc: 'Ordonnancez vos tâches, gérez les dépendances et anticipez les retards en temps réel.' },
    { Icon: ChartBar,     title: 'Rapports automatiques',       desc: 'Hebdomadaires, mensuels, avancement, planning — générés en 1 clic, prêts à envoyer.', accent: 'white' },
    { Icon: Flag,         title: 'Réserves',                    desc: 'Créez, priorisez, illustrez avec photos et notes vocales. Suivi complet du cycle de vie.', accent: 'gold' },
    { Icon: CheckCircle,  title: 'Levée de réserves',           desc: 'Contrôle physique lors des visites, photos de validation, traçabilité complète.' },
    { Icon: FolderOpen,   title: 'Dossier documentaire',        desc: 'Tous vos documents chantier archivés, versionnés, accessibles hors ligne.', accent: 'white' },
    { Icon: ChartBar,     title: 'Tableau de bord',             desc: 'Vue synthétique de vos chantiers, réserves, tâches et échéances — en un seul écran.' },
    { Icon: Bell,         title: 'Notifications intelligentes', desc: 'Vous êtes alerté au bon moment : réserves anciennes, retards, visites imminentes.', accent: 'gold' },
    { Icon: Sparkle,      title: 'AXEL, l\'assistant IA',        desc: 'Votre expert en ligne : analyse, prépare, résume, rédige. Une conversation, une réponse experte.', accent: 'violet' },
    { Icon: Camera,       title: 'Gaby — Analyse photo',         desc: 'Réservé au module CSPS : prenez une photo de chantier, Gaby analyse et propose observations et réserves argumentées.', accent: 'violet' },
  ];

  const benefits = [
    { Icon: Lightning,    title: 'Gain de temps massif',        desc: 'Automatisation des tâches répétitives : rapports, PDF, emails, relances.' },
    { Icon: ClipboardText,title: 'Zéro paperasse',              desc: 'Fini les tableurs et les documents Word disséminés. Tout est dans CHANTIX.' },
    { Icon: Users,        title: 'Centralisation totale',       desc: 'Chantiers, entreprises, réserves, planning, docs : un seul outil.' },
    { Icon: ChartBar,     title: 'Suivi temps réel',            desc: 'Voyez instantanément où vous en êtes sur chacun de vos chantiers.' },
    { Icon: Sparkle,      title: 'Assistance IA intégrée',      desc: 'AXEL pour tous, Gaby dédié aux CSPS : nos IA travaillent avec vos données pour vous faire gagner en clarté.' },
    { Icon: Rocket,       title: 'Évolution continue',          desc: 'Nouvelles fonctionnalités livrées régulièrement, à l\'écoute des pilotes.' },
  ];

  const faqs = [
    { q: 'À qui s\'adresse CHANTIX ?', a: 'Aux coordonnateurs SPS (CSPS), aux Ordonnanceurs-Pilotes-Coordonnateurs (OPC), aux maîtres d\'œuvre et aux entreprises du BTP qui veulent moderniser leur suivi de chantier et gagner du temps sur la partie administrative.' },
    { q: 'Le programme pilote est-il vraiment gratuit ?', a: 'Oui, 100% gratuit pendant toute la durée du pilote (accès complet à toutes les fonctionnalités, y compris AXEL pour tous, et Gaby pour les CSPS). En contrepartie, vous nous aidez à améliorer la plateforme via vos retours. À la fin du pilote, vous bénéficiez de tarifs préférentiels au lancement.' },
    { q: 'Comment fonctionne AXEL ?', a: 'AXEL (Assistant eXpert En Ligne) est un chatbot IA qui a accès à VOS données CHANTIX (chantiers, réserves, planning, comptes rendus). Vous lui posez des questions en français ("Prépare ma visite du chantier X", "Quelles réserves anciennes non levées ?") et il répond en s\'appuyant sur vos données réelles. Il ne fait ni commercial, ni RH, ni comptable — il est 100% dédié au métier BTP.' },
    { q: 'Mes données sont-elles sécurisées ?', a: 'Oui. Vos données sont chiffrées en transit (HTTPS/TLS) et stockées sur des serveurs européens. Les conversations avec AXEL sont chiffrées et ne servent JAMAIS à ré-entraîner les modèles IA (garantie contractuelle avec Anthropic). Vous restez propriétaire de vos données et pouvez les supprimer à tout moment.' },
    { q: 'Comment devenir pilote ?', a: 'Cliquez sur "Rejoindre le programme pilote", créez votre compte gratuit en 2 minutes, et vous êtes automatiquement inclus dans la vague pilote (nombre de places limité). Vous serez recontacté rapidement par notre équipe.' },
    { q: 'Est-ce que CHANTIX fonctionne sur mobile ?', a: 'Oui, CHANTIX est 100% responsive et pensé mobile-first. Vous pouvez prendre des photos, ajouter des réserves et des notes vocales directement depuis le chantier sur votre téléphone.' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white antialiased overflow-x-hidden" data-testid="presentation-page">
      <div className="pointer-events-none fixed inset-0 z-[1] opacity-[0.04] mix-blend-overlay"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />

      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/70 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center">
              <span className="text-black font-black text-sm">C</span>
            </div>
            <span className="font-bold tracking-wide">CHANTIX</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-neutral-300">
            <a href="#features" className="hover:text-[#D4AF37] transition">Fonctionnalités</a>
            <a href="#axel" className="hover:text-[#D4AF37] transition">AXEL</a>
            <a href="#pilot" className="hover:text-[#D4AF37] transition">Programme Pilote</a>
            <a href="#faq" className="hover:text-[#D4AF37] transition">FAQ</a>
            <a href="#contact" className="hover:text-[#D4AF37] transition">Contact</a>
          </div>
          <a href={LOGIN_URL}
            className="text-sm bg-gradient-to-r from-[#D4AF37] to-[#F5C518] text-black font-semibold px-4 py-2 rounded-lg hover:shadow-lg hover:shadow-[#D4AF37]/20 transition"
            data-testid="nav-cta-login">
            Se connecter
          </a>
        </div>
      </nav>

      <section className="relative pt-40 pb-24 px-6 z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/[0.06] via-transparent to-transparent" />
        <div className="absolute top-40 right-10 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-[100px]" />
        <div className="absolute top-60 left-10 w-80 h-80 rounded-full bg-purple-500/10 blur-[100px]" />
        <div className="max-w-6xl mx-auto relative z-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest text-[#D4AF37] uppercase mb-6 border border-[#D4AF37]/30 px-3 py-1 rounded-full">
              ⚡ Nouveau · Assistant IA AXEL intégré
            </span>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="text-5xl md:text-7xl font-black leading-[1.05] tracking-tight mb-6">
              La plateforme intelligente<br />
              dédiée aux <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5C518] to-[#D4AF37] bg-clip-text text-transparent">coordinateurs CSPS, OPC</span><br />
              et aux professionnels du BTP.
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-xl text-neutral-300 max-w-3xl mb-10 leading-relaxed">
              Centralisez vos chantiers, automatisez votre suivi et gagnez un temps précieux
              grâce à l&apos;intelligence artificielle.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="flex flex-wrap gap-4">
              <a href={LOGIN_URL}
                className="group relative inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F5C518] text-black font-bold px-8 py-4 rounded-xl shadow-2xl shadow-[#D4AF37]/20 hover:shadow-[#D4AF37]/40 hover:scale-105 transition-all"
                data-testid="hero-cta-pilot">
                <Rocket size={22} weight="fill" />
                Rejoindre le programme pilote
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="#features"
                className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold px-8 py-4 rounded-xl hover:bg-white/5 hover:border-[#D4AF37]/50 transition-all"
                data-testid="hero-cta-discover">
                <Play size={20} weight="fill" />
                Découvrir CHANTIX
              </a>
            </div>
          </Reveal>
          <Reveal delay={500}>
            <div className="mt-16 grid grid-cols-3 gap-6 max-w-2xl border-t border-white/5 pt-8">
              {[
                { n: '11', l: 'Fonctionnalités clés' },
                { n: 'IA', l: 'Assistant AXEL inclus' },
                { n: '100%', l: 'Gratuit pendant le pilote' },
              ].map((s, i) => (
                <div key={`stat-${i}`}>
                  <p className="text-3xl md:text-4xl font-black text-[#D4AF37]">{s.n}</p>
                  <p className="text-xs uppercase tracking-wider text-neutral-500 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* GEO BANNER — France + Réunion */}
      <section className="py-8 px-6 border-t border-white/5 bg-gradient-to-r from-[#D4AF37]/[0.06] via-black to-[#D4AF37]/[0.06] relative z-10" data-testid="geo-banner">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            {/* Mini carte SVG France + point Réunion */}
            <svg viewBox="0 0 120 60" className="w-28 h-14 flex-shrink-0" aria-hidden="true">
              {/* Silhouette France stylisée (hexagone approximatif) */}
              <path
                d="M 24 12 L 34 8 L 46 10 L 58 6 L 66 14 L 70 22 L 68 34 L 60 46 L 46 50 L 32 46 L 22 38 L 18 26 Z"
                fill="rgba(212, 175, 55, 0.18)"
                stroke="rgba(212, 175, 55, 0.6)"
                strokeWidth="1.2" />
              {/* Point Paris */}
              <circle cx="46" cy="22" r="1.5" fill="#D4AF37" />
              {/* Petit cadre Réunion */}
              <rect x="88" y="30" width="22" height="18" rx="2"
                fill="rgba(212, 175, 55, 0.08)"
                stroke="rgba(212, 175, 55, 0.4)"
                strokeWidth="0.8"
                strokeDasharray="2,1" />
              {/* Île de la Réunion (petit ovale) */}
              <ellipse cx="99" cy="39" rx="4" ry="2.6"
                fill="rgba(212, 175, 55, 0.2)"
                stroke="rgba(212, 175, 55, 0.6)"
                strokeWidth="0.8" />
              {/* Point rouge pulsant sur la Réunion */}
              <circle cx="99" cy="39" r="2.5" fill="#F5C518">
                <animate attributeName="r" values="2.5;4;2.5" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle cx="99" cy="39" r="1.5" fill="#D4AF37" />
              {/* Ligne pointillée Paris — Réunion */}
              <line x1="47" y1="23" x2="98" y2="38"
                stroke="rgba(212, 175, 55, 0.35)"
                strokeWidth="0.7"
                strokeDasharray="1.5,1.5" />
              {/* Label Réunion */}
              <text x="99" y="55" textAnchor="middle"
                fill="rgba(212, 175, 55, 0.8)"
                fontSize="4" fontWeight="600" letterSpacing="0.5">
                RÉUNION
              </text>
            </svg>
            <div>
              <p className="text-sm uppercase tracking-widest text-[#D4AF37] font-bold">Utilisé de la Réunion à la métropole 🌴</p>
              <p className="text-xs text-neutral-400 mt-1">CHANTIX suit vos chantiers partout en France — DOM inclus.</p>
            </div>
          </div>
          <a href={LOGIN_URL}
            className="inline-flex items-center gap-2 text-sm border border-[#D4AF37]/40 text-[#D4AF37] font-semibold px-4 py-2 rounded-lg hover:bg-[#D4AF37]/10 transition"
            data-testid="geo-banner-cta">
            Rejoindre les pilotes <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* TRUST BAR - just after hero */}
      <section className="py-10 px-6 border-t border-white/5 bg-black/40 relative z-10" data-testid="trust-bar">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-6 gap-6">
          {[
            { Icon: GlobeStand,       label: 'Développé en France',    emoji: '🇫🇷' },
            { Icon: Lock,             label: 'Données sécurisées',     emoji: '🔒' },
            { Icon: Cloud,            label: 'Sauvegarde automatique', emoji: '☁️' },
            { Icon: DeviceMobile,     label: 'Multi-device',           emoji: '📱' },
            { Icon: ArrowsClockwise,  label: 'Mises à jour régulières', emoji: '🚀' },
            { Icon: Sparkle,          label: 'IA métier BTP',           emoji: '🤖' },
          ].map((t, i) => (
            <div key={t.label} className="flex flex-col items-center text-center gap-2 group" data-testid={`trust-${i}`}>
              <span className="text-2xl">{t.emoji}</span>
              <p className="text-xs text-neutral-300 group-hover:text-[#D4AF37] transition">{t.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCT SHOWCASE (remplace la vidéo) */}
      <section className="py-24 px-6 border-t border-white/5 relative z-10 overflow-hidden" data-testid="product-showcase">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/5 blur-[120px]" />
        <div className="max-w-6xl mx-auto relative z-10">
          <Reveal>
            <div className="text-center mb-10">
              <span className="text-xs font-semibold tracking-widest text-[#D4AF37] uppercase">Aperçu produit</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-3">Une plateforme conçue pour les pros.</h2>
              <p className="text-neutral-400 mt-3 max-w-2xl mx-auto">
                Trois modules, un seul écosystème. Pensés pour les CSPS et OPC qui veulent gagner du temps sur le terrain.
              </p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                title: 'Tableau de bord',
                caption: 'Vue synthétique de vos chantiers',
                accent: 'from-[#D4AF37]/25',
                Icon: ChartBar,
                mock: (
                  <div className="space-y-2 text-left">
                    <div className="h-2 w-24 bg-[#D4AF37]/40 rounded" />
                    <div className="grid grid-cols-3 gap-1.5 pt-1">
                      <div className="h-12 rounded bg-[#D4AF37]/20 border border-[#D4AF37]/30" />
                      <div className="h-12 rounded bg-purple-500/20 border border-purple-500/30" />
                      <div className="h-12 rounded bg-white/10 border border-white/15" />
                    </div>
                    <div className="pt-2 space-y-1">
                      <div className="h-1.5 w-full bg-white/10 rounded" />
                      <div className="h-1.5 w-5/6 bg-white/10 rounded" />
                      <div className="h-1.5 w-2/3 bg-[#D4AF37]/30 rounded" />
                    </div>
                  </div>
                ),
              },
              {
                title: 'AXEL en action',
                caption: 'Chat IA qui connaît vos données',
                accent: 'from-purple-500/25',
                Icon: Sparkle,
                mock: (
                  <div className="space-y-2 text-left">
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 rounded bg-purple-500 flex-shrink-0" />
                      <div className="flex-1 space-y-1 bg-purple-500/10 rounded p-1.5">
                        <div className="h-1.5 w-full bg-white/20 rounded" />
                        <div className="h-1.5 w-3/4 bg-white/20 rounded" />
                      </div>
                    </div>
                    <div className="flex items-start gap-2 justify-end">
                      <div className="max-w-[70%] space-y-1 bg-[#D4AF37]/20 rounded p-1.5">
                        <div className="h-1.5 w-16 bg-white/30 rounded" />
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 rounded bg-purple-500 flex-shrink-0 animate-pulse" />
                      <div className="flex-1 space-y-1 bg-purple-500/10 rounded p-1.5">
                        <div className="h-1.5 w-full bg-white/20 rounded" />
                        <div className="h-1.5 w-4/5 bg-white/20 rounded" />
                        <div className="h-1.5 w-2/3 bg-white/20 rounded" />
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                title: 'Planning Gantt',
                caption: 'Retards détectés automatiquement',
                accent: 'from-[#D4AF37]/25',
                Icon: CalendarBlank,
                mock: (
                  <div className="space-y-1.5 text-left">
                    {[
                      { w: 'w-full', c: 'bg-emerald-500/40' },
                      { w: 'w-4/5', c: 'bg-[#D4AF37]/50' },
                      { w: 'w-3/5', c: 'bg-red-500/50' },
                      { w: 'w-2/3', c: 'bg-[#D4AF37]/40' },
                      { w: 'w-1/2', c: 'bg-white/20' },
                    ].map((b, k) => (
                      <div key={k} className="flex items-center gap-1.5">
                        <div className="h-1.5 w-10 bg-white/10 rounded" />
                        <div className={`h-2 ${b.w} ${b.c} rounded`} />
                      </div>
                    ))}
                  </div>
                ),
              },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 100}>
                <div className={`rounded-2xl border border-white/10 bg-gradient-to-br ${s.accent} to-transparent p-5 hover:-translate-y-1 hover:border-[#D4AF37]/40 transition-all group`}
                  data-testid={`showcase-${i}`}>
                  {/* Fake browser chrome */}
                  <div className="flex items-center gap-1.5 mb-4 pb-3 border-b border-white/10">
                    <span className="w-2 h-2 rounded-full bg-red-400" />
                    <span className="w-2 h-2 rounded-full bg-amber-400" />
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    <div className="ml-2 h-1.5 w-24 bg-white/10 rounded" />
                  </div>
                  {/* Mockup body */}
                  <div className="h-40 bg-black/50 rounded-lg p-4 group-hover:scale-[1.02] transition-transform origin-center">
                    {s.mock}
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <s.Icon size={20} weight="duotone" className="text-[#D4AF37]" />
                    <div>
                      <p className="font-semibold text-white text-sm">{s.title}</p>
                      <p className="text-xs text-neutral-500">{s.caption}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 px-6 border-t border-white/5 bg-gradient-to-b from-black to-[#0a0a0a] relative z-10" data-testid="stats-section">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest text-[#D4AF37] uppercase">CHANTIX en chiffres</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-3">Une plateforme qui délivre.</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { n: '+8h',   l: 'Gagnées par semaine',    sub: 'sur les tâches admin' },
              { n: '+500',  l: 'Rapports générés',       sub: 'depuis le lancement' },
              { n: '+80',   l: 'Chantiers suivis',        sub: 'sur la plateforme' },
              { n: '+30',   l: 'Pilotes actifs',          sub: 'programme en cours' },
            ].map((s, i) => (
              <Reveal key={s.l} delay={i * 80}>
                <div className="border border-[#D4AF37]/20 rounded-2xl p-6 text-center bg-gradient-to-br from-[#D4AF37]/[0.05] to-transparent hover:border-[#D4AF37]/50 hover:-translate-y-1 transition-all"
                  data-testid={`stat-${i}`}>
                  <p className="text-4xl md:text-5xl font-black text-[#D4AF37]">{s.n}</p>
                  <p className="text-sm text-white font-semibold mt-2">{s.l}</p>
                  <p className="text-xs text-neutral-500 mt-1">{s.sub}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 border-t border-white/5 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Une plateforme moderne pour <span className="text-[#D4AF37]">simplifier</span> vos chantiers.
            </h2>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-lg text-neutral-300 leading-relaxed max-w-4xl mx-auto">
              CHANTIX accompagne les coordonnateurs CSPS, OPC, maîtres d&apos;œuvre et entreprises du BTP
              dans la gestion quotidienne de leurs chantiers. Elle centralise les informations,
              automatise les tâches répétitives et facilite le suivi des opérations grâce à l&apos;intelligence artificielle.
            </p>
          </Reveal>
        </div>
      </section>

      <section id="features" className="py-24 px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-xs font-semibold tracking-widest text-[#D4AF37] uppercase">Fonctionnalités</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-3">Tout ce dont vous avez besoin,<br />au même endroit.</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <FeatureCard key={f.title} {...f} delay={i * 60} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 border-t border-white/5 bg-gradient-to-b from-[#D4AF37]/[0.02] to-transparent relative z-10">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-xs font-semibold tracking-widest text-[#D4AF37] uppercase">Pourquoi CHANTIX</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-3">6 bonnes raisons<br />d&apos;essayer CHANTIX.</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((b, i) => (
              <Reveal key={b.title} delay={i * 80}>
                <Benefit {...b} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="axel" className="py-24 px-6 border-t border-white/5 relative overflow-hidden z-10">
        <div className="absolute top-1/2 -translate-y-1/2 left-0 w-96 h-96 rounded-full bg-purple-600/20 blur-[120px]" />
        <div className="absolute top-1/2 -translate-y-1/2 right-0 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-[120px]" />
        <div className="max-w-6xl mx-auto relative z-10 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative w-fit">
              <img src={AXEL_AVATAR} alt="AXEL"
                className="w-72 h-72 md:w-96 md:h-96 rounded-3xl object-cover shadow-2xl ring-4 ring-purple-500/20" />
              <div className="absolute -bottom-3 -right-3 bg-purple-600 rounded-full w-8 h-8 border-4 border-black flex items-center justify-center">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              </div>
              <div className="absolute -top-6 -left-6 bg-black border border-purple-500/40 rounded-xl px-3 py-1.5 text-xs font-semibold text-purple-300">
                <Sparkle size={12} weight="fill" className="inline mr-1 text-purple-400" />
                Assistant eXpert En Ligne
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal delay={100}>
              <span className="text-xs font-semibold tracking-widest text-purple-400 uppercase">Assistant IA intégré</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-3 mb-6">
                Découvrez <span className="bg-gradient-to-r from-purple-400 to-[#D4AF37] bg-clip-text text-transparent">AXEL</span>,<br />
                votre expert en ligne.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="text-lg text-neutral-300 mb-6 leading-relaxed">
                AXEL n&apos;est pas un simple chatbot. C&apos;est un <strong className="text-white">véritable assistant personnel</strong> qui connaît
                vos chantiers, vos réserves, votre planning et vos comptes rendus.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <ul className="space-y-3 mb-8">
                {[
                  'Organise votre activité au quotidien',
                  'Rappelle vos visites et échéances',
                  'Suit vos réserves anciennes',
                  'Prépare vos missions et visites',
                  'Répond à vos questions métier',
                  'Analyse vos données en temps réel',
                  'Produit des résumés et synthèses',
                ].map((line) => (
                  <li key={line} className="flex items-center gap-3 text-neutral-300">
                    <CheckCircle size={18} weight="fill" className="text-purple-400 flex-shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={400}>
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-8">
                <p className="text-sm text-purple-200">
                  <Camera size={16} weight="fill" className="inline mr-1 text-purple-400" />
                  <strong>Gaby</strong> — <span className="text-purple-300/80 text-xs uppercase tracking-wider font-bold">Module CSPS uniquement</span> — complète AXEL en analysant les photos de vos chantiers
                  pour proposer observations et assistance réglementaire SPS.
                </p>
              </div>
              <a href={LOGIN_URL}
                className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-xl transition"
                data-testid="axel-cta">
                <Sparkle size={18} weight="fill" /> Tester AXEL gratuitement
                <ArrowRight size={16} />
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-24 px-6 border-t border-white/5 relative z-10" data-testid="comparison-section">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D4AF37] uppercase">Pourquoi choisir CHANTIX ?</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-3">La différence est <span className="text-[#D4AF37]">immédiate.</span></h2>
              <p className="text-neutral-400 mt-3 max-w-2xl mx-auto text-base md:text-lg">Voyez ce que vous gagnez concrètement au quotidien.</p>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-black/40 backdrop-blur">
              <div className="grid grid-cols-3">
                <div className="p-3 md:p-5 border-r border-white/10 bg-black/40">
                  <p className="text-[10px] md:text-xs uppercase tracking-widest text-neutral-500 font-semibold">Critère</p>
                </div>
                <div className="p-3 md:p-5 border-r border-white/10 bg-black/40 text-center">
                  <p className="text-[10px] md:text-xs uppercase tracking-widest text-neutral-500 font-semibold">Sans CHANTIX</p>
                  <p className="text-[9px] md:text-[10px] text-neutral-600 mt-1">Excel · mails · papier</p>
                </div>
                <div className="p-3 md:p-5 bg-gradient-to-b from-[#D4AF37]/15 to-transparent text-center">
                  <p className="text-[10px] md:text-xs uppercase tracking-widest text-[#D4AF37] font-bold">Avec CHANTIX</p>
                  <p className="text-[9px] md:text-[10px] text-neutral-500 mt-1">SaaS + IA</p>
                </div>
              </div>
              {[
                { crit: 'Rédaction d\'un compte rendu', old: '⏱ 45 à 90 min', neu: '✨ 5 min · IA + auto',            hero: true },
                { crit: 'Suivi des réserves',            old: '📋 Tableur, oublis fréquents', neu: '🚨 Rappels auto + photos' },
                { crit: 'Génération PDF (rapport, PGC…)',old: '🧾 Copier-coller Word', neu: '📄 1 clic · charte respectée', hero: true },
                { crit: 'Centralisation des documents',  old: '📁 Dossiers dispersés', neu: '☁️ Dossier documentaire versionné' },
                { crit: 'Planning et retards',           old: '📆 Ms Project figé', neu: '📊 Gantt interactif + alertes' },
                { crit: 'Assistance métier',             old: '❌ Aucune',            neu: '🤖 AXEL 24h/24 · vos données',       hero: true },
                { crit: 'Mobilité chantier',             old: '📵 Papier ou mail',    neu: '📱 App mobile · photo · vocal' },
                { crit: 'Conformité CSPS',               old: '⚠️ Templates génériques', neu: '✅ PGC/PPSPS/DIUO générés' },
                { crit: 'Temps admin par semaine',       old: '⌛ 12 à 15 h',         neu: '⚡ 4 à 6 h · économie ~8h/sem',        hero: true, big: true },
              ].map((row, i) => (
                <div key={row.crit}
                  className={`grid grid-cols-3 border-t border-white/5 ${row.hero ? 'bg-[#D4AF37]/[0.04]' : i % 2 === 0 ? 'bg-white/[0.01]' : ''} ${row.big ? 'ring-1 ring-[#D4AF37]/40' : ''}`}
                  data-testid={`comp-row-${i}`}>
                  <div className={`p-3 md:p-4 border-r border-white/5 font-semibold text-white text-sm md:text-base ${row.big ? 'text-[#D4AF37]' : ''}`}>
                    {row.hero && <span className="inline-block mr-1.5 align-middle text-[#D4AF37]">★</span>}
                    {row.crit}
                  </div>
                  <div className="p-3 md:p-4 border-r border-white/5 text-center text-neutral-400 text-sm md:text-base">{row.old}</div>
                  <div className={`p-3 md:p-4 text-center text-sm md:text-base font-semibold bg-[#D4AF37]/[0.06] ${row.big ? 'text-[#F5C518] text-base md:text-lg font-bold' : 'text-[#F5C518]'}`}>{row.neu}</div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Punchline */}
          <Reveal delay={200}>
            <div className="mt-10 text-center">
              <p className="text-xl md:text-2xl font-semibold text-white leading-relaxed max-w-3xl mx-auto">
                Chaque heure économisée est une heure de plus consacrée à vos chantiers,
                <br className="hidden md:block" />
                <span className="text-[#D4AF37]"> et non à l&apos;administratif.</span>
              </p>
            </div>
          </Reveal>

          {/* CTA */}
          <Reveal delay={300}>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href={LOGIN_URL}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F5C518] text-black font-bold text-base md:text-lg px-8 py-4 rounded-xl shadow-2xl shadow-[#D4AF37]/20 hover:scale-105 hover:shadow-[#D4AF37]/40 transition-all"
                data-testid="cta-try-comparison">
                <Rocket size={20} weight="fill" /> Rejoindre le programme pilote
                <ArrowRight size={18} />
              </a>
              <p className="text-xs text-neutral-500 sm:ml-4">Inscription en 2 min · sans carte bancaire</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PILOT MOTIVATIONS (remplace les témoignages) */}
      <section className="py-24 px-6 border-t border-white/5 relative z-10" data-testid="motivations-section">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest text-[#D4AF37] uppercase">Programme pilote</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-3">Pourquoi nos pilotes<br />nous ont rejoint.</h2>
              <p className="text-neutral-400 mt-3 max-w-2xl mx-auto">
                Trois motivations que nous entendons systématiquement chez les coordonnateurs qui rejoignent CHANTIX.
              </p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                Icon: Lightning,
                accent: 'gold',
                title: 'Gagner du temps sur les rapports',
                desc: 'Fini les comptes rendus rédigés à 22h. En 5 minutes, un CR complet est prêt à envoyer au MOA.',
                metric: '+8h/semaine',
              },
              {
                Icon: Flag,
                accent: 'white',
                title: 'Centraliser les réserves',
                desc: 'Une seule plateforme pour toutes les réserves, avec photos, notes vocales et rappels automatiques.',
                metric: '0 oubli',
              },
              {
                Icon: Sparkle,
                accent: 'violet',
                title: 'Se démarquer avec l\'IA',
                desc: 'Le premier assistant BTP qui connaît vos données. Une longueur d\'avance dès la prochaine consultation.',
                metric: 'Différenciant',
              },
            ].map((m, i) => {
              const bg = {
                gold:   'from-[#D4AF37]/15 border-[#D4AF37]/30 text-[#D4AF37]',
                white:  'from-white/10 border-white/20 text-white',
                violet: 'from-purple-500/20 border-purple-500/30 text-purple-400',
              }[m.accent];
              return (
                <Reveal key={m.title} delay={i * 100}>
                  <div className={`rounded-2xl border p-6 bg-gradient-to-br ${bg} to-transparent hover:-translate-y-1 transition-transform h-full flex flex-col`}
                    data-testid={`motivation-${i}`}>
                    <div className="w-12 h-12 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center mb-4">
                      <m.Icon size={22} weight="duotone" />
                    </div>
                    <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${m.accent === 'gold' ? 'text-[#D4AF37]' : m.accent === 'violet' ? 'text-purple-400' : 'text-white/70'}`}>
                      {m.metric}
                    </p>
                    <h3 className="text-white font-semibold text-lg mb-2">{m.title}</h3>
                    <p className="text-neutral-400 text-sm leading-relaxed flex-1">{m.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={300}>
            <div className="mt-10 text-center">
              <a href="#contact"
                className="inline-flex items-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] font-semibold px-6 py-3 rounded-xl hover:bg-[#D4AF37]/10 transition"
                data-testid="motivations-contact-cta">
                <EnvelopeSimple size={18} weight="fill" /> Nous écrire un message
                <ArrowRight size={16} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST LOGOS */}
      <section className="py-16 px-6 border-t border-white/5 bg-black/30 relative z-10" data-testid="trust-logos">
        <div className="max-w-6xl mx-auto text-center">
          <Reveal>
            <span className="text-xs font-semibold tracking-widest text-neutral-500 uppercase">Ils nous font confiance</span>
          </Reveal>
          <Reveal delay={150}>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="relative aspect-[3/2] rounded-xl border border-[#D4AF37]/30 bg-white/95 flex items-center justify-center p-4 hover:scale-105 transition group"
                data-testid="logo-m2b-run" title="M2B RUN — Pilote CHANTIX depuis l'île de la Réunion">
                <img src="/logos/m2b-run.jpg" alt="M2B RUN"
                  className="max-h-full max-w-full object-contain" />
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black border border-[#D4AF37]/40 rounded-full px-2.5 py-0.5 text-[10px] text-[#D4AF37] font-semibold whitespace-nowrap opacity-90">
                  <span>🌴</span>
                  <span>Île de la Réunion</span>
                </div>
              </div>
              {[2, 3, 4, 5].map((i) => (
                <div key={i} className="aspect-[3/2] rounded-xl border border-dashed border-white/10 bg-white/[0.02] flex items-center justify-center"
                  data-testid={`logo-slot-${i}`}>
                  <span className="text-[10px] uppercase tracking-widest text-neutral-600">Logo pilote {i}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-neutral-500 mt-10 italic text-center">
              De la métropole à l&apos;outre-mer — CHANTIX accompagne les pros du BTP partout en France.
            </p>
          </Reveal>
        </div>
      </section>

      <section id="pilot" className="py-24 px-6 border-t border-white/5 relative z-10">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D4AF37] uppercase">Programme Pilote</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-3">Rejoignez la vague pilote CHANTIX.</h2>
              <p className="text-lg text-neutral-400 mt-4 max-w-2xl mx-auto">
                Bénéficiez d&apos;un accès gratuit complet et participez à la construction d&apos;une plateforme métier faite <em>pour</em> les pros du BTP, <em>par</em> les pros du BTP.
              </p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {[
              { Icon: Lightning,   title: 'Accès complet gratuit', desc: 'Toutes les fonctionnalités incluses pendant toute la durée du pilote.' },
              { Icon: ChatCircleDots, title: 'Contact direct avec l\'équipe', desc: 'Vos retours sont traités en priorité et façonnent le produit.' },
              { Icon: Rocket,      title: 'Accès anticipé aux nouveautés', desc: 'Vous testez en avant-première AXEL (tous), Gaby (CSPS), et toutes les futures features.' },
              { Icon: Certificate, title: 'Tarifs préférentiels au lancement', desc: 'Statut privilégié à vie et remise conséquente sur les futurs plans.' },
            ].map((b, i) => (
              <Reveal key={b.title} delay={i * 80}>
                <div className="border border-[#D4AF37]/20 rounded-2xl p-6 bg-gradient-to-br from-[#D4AF37]/[0.04] to-transparent hover:border-[#D4AF37]/40 transition">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                      <b.Icon size={20} weight="duotone" className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg mb-1">{b.title}</h3>
                      <p className="text-neutral-400 text-sm">{b.desc}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={200}>
            <div className="text-center">
              <a href={LOGIN_URL}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F5C518] text-black font-bold px-8 py-4 rounded-xl shadow-2xl shadow-[#D4AF37]/20 hover:scale-105 transition-all"
                data-testid="pilot-cta">
                <Rocket size={20} weight="fill" />
                Je deviens pilote
                <ArrowRight size={18} />
              </a>
              <p className="text-xs text-neutral-500 mt-3">Inscription en 2 minutes · Sans carte bancaire</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SCREENSHOTS CAROUSEL */}
      <ScreenshotsCarousel />

      <section id="faq" className="py-24 px-6 border-t border-white/5 relative z-10">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest text-[#D4AF37] uppercase">Questions fréquentes</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-3">Vos questions,<br />nos réponses.</h2>
            </div>
          </Reveal>
          <Reveal>
            <div>
              {faqs.map((f, i) => (
                <FAQItem key={`faq-${i}`} idx={i} q={f.q} a={f.a} open={openFaq} setOpen={setOpenFaq} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section id="contact" className="py-24 px-6 border-t border-white/5 bg-gradient-to-b from-black to-[#0a0a0a] relative z-10">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12">
          <Reveal>
            <div>
              <span className="text-xs font-semibold tracking-widest text-[#D4AF37] uppercase">Contact</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-3 mb-6">Une question ?<br />Parlons-en.</h2>
              <p className="text-neutral-400 mb-8 leading-relaxed">
                Que vous soyez CSPS, OPC, maître d&apos;œuvre ou dirigeant d&apos;entreprise du BTP,
                l&apos;équipe CHANTIX (GABRYX) est à votre écoute pour vous accompagner.
              </p>
              <ul className="space-y-4 text-sm">
                <li className="flex items-center gap-3 text-neutral-300">
                  <EnvelopeSimple size={18} className="text-[#D4AF37]" />
                  <a href="mailto:gabryx@chantix.pro" className="hover:text-[#D4AF37] transition">gabryx@chantix.pro</a>
                </li>
                <li className="flex items-center gap-3 text-neutral-300">
                  <Certificate size={18} className="text-[#D4AF37]" />
                  <span>Édité par <strong className="text-white">GABRYX</strong></span>
                </li>
                <li className="flex items-center gap-3 text-neutral-300">
                  <MapPin size={18} className="text-[#D4AF37]" />
                  <span>France</span>
                </li>
              </ul>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <form onSubmit={submitContact} className="border border-white/10 rounded-2xl p-6 bg-black/40 backdrop-blur"
              data-testid="contact-form">
              <div className="space-y-4">
                <div>
                  <label htmlFor="cx-name" className="block text-xs uppercase tracking-wider text-neutral-400 mb-1">Nom *</label>
                  <input id="cx-name" type="text" required
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37]/50 focus:outline-none transition"
                    data-testid="contact-name" />
                </div>
                <div>
                  <label htmlFor="cx-email" className="block text-xs uppercase tracking-wider text-neutral-400 mb-1">Email *</label>
                  <input id="cx-email" type="email" required
                    value={form.email}
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37]/50 focus:outline-none transition"
                    data-testid="contact-email" />
                </div>
                <div>
                  <label htmlFor="cx-company" className="block text-xs uppercase tracking-wider text-neutral-400 mb-1">Entreprise</label>
                  <input id="cx-company" type="text"
                    value={form.company}
                    onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37]/50 focus:outline-none transition"
                    data-testid="contact-company" />
                </div>
                <div>
                  <label htmlFor="cx-message" className="block text-xs uppercase tracking-wider text-neutral-400 mb-1">Message *</label>
                  <textarea id="cx-message" required rows={4}
                    value={form.message}
                    onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37]/50 focus:outline-none transition resize-none"
                    data-testid="contact-message" />
                </div>
                <button type="submit" disabled={sending}
                  className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F5C518] text-black font-bold py-3.5 rounded-lg hover:shadow-lg hover:shadow-[#D4AF37]/20 disabled:opacity-60 transition"
                  data-testid="contact-submit">
                  {sending ? 'Envoi...' : 'Envoyer'}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      <footer className="py-10 px-6 border-t border-white/5 bg-black relative z-10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-neutral-500">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center">
              <span className="text-black font-black text-[10px]">C</span>
            </div>
            <span>© 2026 CHANTIX — Édité par GABRYX</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/cgu" className="hover:text-[#D4AF37] transition">CGU</Link>
            <Link to="/cgv" className="hover:text-[#D4AF37] transition">CGV</Link>
            <Link to="/confidentialite" className="hover:text-[#D4AF37] transition">Confidentialité</Link>
            <Link to="/mentions-legales" className="hover:text-[#D4AF37] transition">Mentions légales</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
